import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
  HANDOVER_TYPE_HO3_ID,
  PERMISSION_ENGINEERING_COMPLETION,
} from 'src/app/handover-constants';
import { Handover } from 'src/app/interfaces/handover';
import {
  BarrierElement,
  BarrierElementProblem,
  BarrierElementRelationProblem,
} from 'src/app/interfaces/well-integrity';
import { DialogService } from 'src/app/services/dialog.service';
import { HandoverService } from 'src/app/services/handover.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SectionTouched } from '../../shared/section-touched.class';
import { CasingLiner } from 'src/app/interfaces/casing-liner';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-handover-detail-well-integrity-barrier-element-issues',
  templateUrl:
    './handover-detail-well-integrity-barrier-element-issues.component.html',
  styleUrls: [
    './handover-detail-well-integrity-barrier-element-issues.component.scss',
  ],
  standalone: false,
})
export class HandoverDetailWellIntegrityBarrierElementIssuesComponent
  extends SectionTouched
  implements OnInit
{
  @Input() handover: Handover | undefined;
  @Input() readOnlyContent!: boolean;
  @Output() isLoading = new EventEmitter<boolean>();

  barrierElements: BarrierElement[] = [];
  reportedBarrierElements: BarrierElementProblem[] = [];
  barrierElementsRelation: BarrierElementRelationProblem[] = [];
  originalBarrierElementsRelation: BarrierElementRelationProblem[] = [];
  originalsReportedBarrierComments: string[] = [];
  chosenBarrierElement: BarrierElement = {} as BarrierElement;
  problemDescriptionIsEnabled = false;
  problemDescription = '';
  problemDescriptionMinLength = 3;
  problemSubmissionIsEnabled = false;
  casingCount = 0;
  isDisabled: boolean[] = [];
  showFooterNoDataOrign = false;
  hideFooter = true;
  hasBarrierElementRelation = false;
  isUserFromEngineering = false;

  displayedColumns = [
    'name',
    'problemDate',
    'testPressure',
    'testApproved',
    'actions',
  ];

  constructor(
    public handoverService: HandoverService,
    public dialogService: DialogService,
    public notificationService: NotificationService,
    private userService: UserService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getBarrierElements();
    this.getBarrierElementsRelation();
    this.isUserFromEngineering = this.userService.hasPermission(
      PERMISSION_ENGINEERING_COMPLETION
    );
  }

  enableComment(): void {
    this.problemDescriptionIsEnabled = undefined != this.chosenBarrierElement;
    this.emitPendingChanges(
      this.problemDescriptionIsEnabled && this.problemSubmissionIsEnabled
    );
  }

  enableCommentSubmission(): void {
    this.problemSubmissionIsEnabled =
      this.problemDescription.length >= this.problemDescriptionMinLength;
    this.emitPendingChanges(
      this.problemDescriptionIsEnabled && this.problemSubmissionIsEnabled
    );
  }

  submitBarrierProblem(): void {
    if (!this.handover?.handoverId) {
      return;
    }
    this.isLoading.emit(true);
    const problem = {
      element: this.chosenBarrierElement,
      comment: this.problemDescription,
      status: 'new',
    } as BarrierElementProblem;
    this.handoverService
      .saveBarrierElementProblem(this.handover?.handoverId, problem)
      .subscribe((savedProblem: BarrierElementProblem) => {
        // Retrieve reports and filter already reported
        this.getBarrierElements();
        // Set blank the comment field and disable related controls
        this.problemDescription = '';
        this.problemDescriptionIsEnabled = false;
        this.problemSubmissionIsEnabled = false;
        this.emitPendingChanges(false);
        this.isLoading.emit(false);
      });
  }

  updateBarrierProblem(
    problem: BarrierElementProblem,
    formGroup: UntypedFormGroup
  ): void {
    if (!this.handover?.handoverId) {
      return;
    }
    this.isLoading.emit(true);
    problem.status = 'edited';
    this.handoverService
      .saveBarrierElementProblem(this.handover?.handoverId, problem)
      .subscribe((savedProblem: BarrierElementProblem) => {
        formGroup.markAsPristine();
        this.emitPendingChanges(false);
        this.isLoading.emit(false);
      });
  }

  removeBarrierProblem(elementToRemove: BarrierElementProblem): void {
    const dialogRef = this.dialogService.openConfirmDialog(
      'Problemas de Elementos de Barreras',
      `¿Está seguro de que desea quitar este problema del reporte?`
    );
    dialogRef.afterClosed().subscribe((confirmRemove) => {
      if (confirmRemove && this.handover?.handoverId) {
        this.isLoading.emit(true);
        // Change the status and mark it to be removed
        elementToRemove.status = 'deleted';
        this.handoverService
          .saveBarrierElementProblem(this.handover?.handoverId, elementToRemove)
          .subscribe((savedProblem: BarrierElementProblem) => {
            this.isLoading.emit(false);
            if (savedProblem) {
              // Update elements and reports from backend
              this.getBarrierElements();
              // Notify success
              this.notificationService.notify(
                'Reporte de problema de elemento de barrera quitado satisfactoriamente',
                true
              );
            }
          });
      }
    });
  }

  /**
   * Retrieves barrier elements and reported problems from data source,
   * and filters away already reported items from barrierElements.
   */
  getBarrierElements(): void {
    this.isLoading.emit(true);
    this.handoverService
      .getBarrierElements(this.handover?.handoverTypeId == HANDOVER_TYPE_HO3_ID)
      .subscribe(
        (values: BarrierElement[]) => {
          this.barrierElements = values;
          // Retrieve reported issues
          if (this.handover?.handoverId) {
            this.handoverService
              .getBarrierElementProblems(this.handover?.handoverId)
              .subscribe((values: BarrierElementProblem[]) => {
                this.reportedBarrierElements = values;
                this.setBarrierElements();
                this.originalsReportedBarrierComments = values.map(
                  (v) => v.comment
                );
                this.filterBarrierElements();
                this.isLoading.emit(false);
              });
          }
        },
        () => {
          this.isLoading.emit(false);
        }
      );
  }

  /**
   * Helps filtering already reported elements away from
   * this.barrierElements collection.
   */
  filterBarrierElements(): void {
    this.reportedBarrierElements.forEach((reportedElement) => {
      this.barrierElements = this.barrierElements.filter((element) => {
        return (
          element.barrierElementId != reportedElement.element.barrierElementId
        );
      });
    });
  }

  commentUpdated(index: number, comment: string): void {
    this.emitPendingChanges(
      this.originalsReportedBarrierComments[index] != comment &&
        comment.length >= 3
    );
  }

  getBarrierElementsRelation(): void {
    this.isLoading.emit(true);
    if (this.handover?.handoverId) {
      this.handoverService
        .getCasingLiner(
          this.handover?.handoverId,
          this.handover?.wellId,
          this.handover?.handoverStatusId,
          this.handover?.handoverTypeId
        )
        .subscribe((values: CasingLiner[]) => {
          this.casingCount = values.length;
          if (this.handover?.handoverId) {
            this.handoverService
              .getBarrierElementRelationProblems(
                this.handover?.handoverId,
                this.casingCount
              )
              .subscribe((barrierValues: BarrierElementRelationProblem[]) => {
                this.barrierElementsRelation = barrierValues;
                this.setOriginalBarrierElementsRelation(barrierValues);
                this.hideFooter = barrierValues.length > 0;
                this.setBarrierElements();
                this.isLoading.emit(false);
              });
          }
        });
    }
  }

  openComments(
    report: BarrierElementRelationProblem,
    index: number,
    event: any,
    readOnly: boolean
  ): void {
    const target = new ElementRef(event.currentTarget);
    this.dialogService
      .openCommentDialog(
        'Indique detalle de elemento de barrera',
        report?.comment,
        target,
        -200,
        -450,
        readOnly || this.canChangeBarrierElements(),
        1000
      )
      .afterClosed()
      .subscribe((comments: string) => {
        this.barrierElementsRelation[index!].comment = comments;
        this.enableSaveButton(index);
      });
  }

  enableSaveButton(i: number): void {
    const oSp = this.originalBarrierElementsRelation[i];
    const enable =
      this.barrierElementsRelation[i].comment != oSp.comment ||
      this.barrierElementsRelation[i].problemDate != oSp.problemDate ||
      this.barrierElementsRelation[i].testApproved != oSp.testApproved ||
      this.barrierElementsRelation[i].testPressure != oSp.testPressure;
    this.isDisabled[i] = !enable;
    this.reportPendingChanges();
  }

  reportPendingChanges(): void {
    let thereAreChanges = false;
    for (let index = 0; index < this.barrierElementsRelation.length; index++) {
      thereAreChanges =
        thereAreChanges ||
        this.barrierElementsRelation[index].comment !=
          this.originalBarrierElementsRelation[index].comment ||
        this.barrierElementsRelation[index].problemDate !=
          this.originalBarrierElementsRelation[index].problemDate ||
        this.barrierElementsRelation[index].testApproved !=
          this.originalBarrierElementsRelation[index].testApproved ||
        this.barrierElementsRelation[index].testPressure !=
          this.originalBarrierElementsRelation[index].testPressure;
    }
    this.emitPendingChanges(thereAreChanges);
  }

  setOriginalBarrierElementsRelation(
    values: BarrierElementRelationProblem[]
  ): void {
    this.originalBarrierElementsRelation = values.map((value) => {
      this.isDisabled.push(true);

      if (value.comment == null) value.comment = '';
      return Object.assign({ isDisabled: true }, value);
    });
  }

  save(): void {
    const postBarrierElementRelation: BarrierElementRelationProblem[] = [];
    this.isLoading.emit(true);
    for (let i = 0; i < this.barrierElementsRelation.length; i++) {
      if (this.handover) {
        this.barrierElementsRelation[i].handoverId = this.handover.handoverId!;
        const value = this.barrierElementsRelation[i].testPressure as any;
        this.barrierElementsRelation[i].testPressure =
          value === "" ? null : value;
        postBarrierElementRelation.push(this.barrierElementsRelation[i]);
      }
    }
    if (postBarrierElementRelation.length > 0) {
      if (this.handover) {
        this.handoverService
          .saveBarrierElementRelationProblems(
            this.handover.handoverId!,
            postBarrierElementRelation
          )
          .subscribe(
            (
              savedBarrierElementRelationProblems: BarrierElementRelationProblem[]
            ) => {
              if (savedBarrierElementRelationProblems) {
                this.notificationService.notify(
                  `Los cambios en Elementos de barrera se han guardado con éxito.`,
                  true
                );
                this.originalBarrierElementsRelation = [];
                this.emitPendingChanges(false);
                this.getBarrierElementsRelation();
              } else {
                this.notificationService.notify(
                  `Ocurrió un error al grabar los cambios en Elementos de barrera.`,
                  false
                );
              }
            }
          );
      }
    }
    this.isLoading.emit(false);
  }

  setBarrierElements() {
    this.barrierElementsRelation.forEach((element) => {
      if (element.handoverId != null) {
        this.hasBarrierElementRelation = true;
        return;
      }
    });
    if (
      !this.hasBarrierElementRelation &&
      this.handover?.handoverStatusId == 4 &&
      this.reportedBarrierElements.length > 0
    ) {
      this.barrierElementsRelation = [];
      this.reportedBarrierElements.map((element) => {
        const barrierElementRelation = {
          name: element.element.name,
          comment: element.comment,
        } as BarrierElementRelationProblem;
        this.barrierElementsRelation.push(barrierElementRelation);
        this.hideFooter = this.barrierElementsRelation.length > 0;
      });
    }
  }

  canChangeBarrierElements(): boolean {
    let canChange = false;
    if (
      this.handover?.handoverTypeId == HANDOVER_TYPE_HO3_ID &&
      !this.isUserFromEngineering
    ) {
      canChange = true;
    }
    return canChange;
  }
}
